/**
 * Visit Clinic Invoice API service (来院用領収書(窓口))
 *
 * A2 list data comes from the Continuation API; R2 has its own endpoint here, because
 * the 継続受診者登録 module it belongs to is A2-only. Both return the same row shape.
 * PDF printing is shared.
 */

import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import type {
  ContinuationListResponse,
  ContinuationListRow,
} from '@/services/main/continuation/continuationService';
import type { VisitClinicInvoicePrintPayload } from './types';

/**
 * Print visit clinic invoice PDF (領収書 / 保険外明細 / 月別 / format).
 * Returns a Blob that can be opened in a new browser tab.
 */
export async function printVisitClinicInvoice(
  siteType: string,
  clinicNumber: string,
  payload: VisitClinicInvoicePrintPayload
): Promise<Blob> {
  const path = clinicApiPath(siteType, clinicNumber, '/visit-clinic-invoices/print');
  const res = await apiClient.post(path, payload, {
    responseType: 'blob',
  });
  return res.data as Blob;
}

/**
 * Same shape as the A2 list, with everything the R2 endpoint does not send left optional,
 * so the page can hold either response in one variable.
 */
export type R2VisitClinicInvoiceListData = Partial<ContinuationListResponse['data']> & {
  list: ContinuationListRow[];
};

/**
 * POST /visit-clinic-invoices/r2-list — SCR-182 list for R2.
 *
 * Returns only `list`; the page derives 合計 and the row count from it, exactly as it
 * already does when the A2 response omits `totals` / `pagination`.
 */
export async function fetchR2VisitClinicInvoiceList(
  siteType: string,
  clinicNumber: string,
  params: Record<string, unknown>
): Promise<R2VisitClinicInvoiceListData> {
  const res = await apiClient.post<{ success: boolean; data: R2VisitClinicInvoiceListData }>(
    clinicApiPath(siteType, clinicNumber, '/visit-clinic-invoices/r2-list'),
    params
  );
  return res.data.data;
}

/**
 * GET /visit-clinic-invoices/print-reset/{visitId} — SCR-182 §1.12 for R2.
 *
 * A2 resets through patients/continuation/print-reset, which writes `visits`; this one
 * writes `r_visits`, so the R2 list actually clears its 「済」 mark.
 */
export async function printResetR2VisitClinicInvoice(
  siteType: string,
  clinicNumber: string,
  visitId: number
): Promise<{ response: boolean }> {
  const res = await apiClient.get<{ success: boolean; data: { response: boolean } }>(
    clinicApiPath(siteType, clinicNumber, `/visit-clinic-invoices/print-reset/${visitId}`)
  );
  return res.data.data;
}

/**
 * GET /visit-clinic-invoices/change-price/{visitId}/{freeMedicalFee} — SCR-182 §1.13 for R2.
 *
 * Same as the A2 continuation endpoint, against `r_visits`.
 */
export async function changePriceR2VisitClinicInvoice(
  siteType: string,
  clinicNumber: string,
  visitId: number,
  freeMedicalFee: number
): Promise<{ response: boolean; diff?: number }> {
  const res = await apiClient.get<{ success: boolean; data: { response: boolean; diff?: number } }>(
    clinicApiPath(
      siteType,
      clinicNumber,
      `/visit-clinic-invoices/change-price/${visitId}/${freeMedicalFee}`
    )
  );
  return res.data.data;
}

/**
 * SCR-183 領収明細編集 — the modal payload for one 来院 row.
 *
 * Same envelope as the A2 continuation edit data, with R2's fee lines: 施術料 /
 * 変形徒手矯正術 / 温罨法 / 温罨・電気光線器具 / 明細書発行加算 / 施術報告書交付料.
 */
export interface R2VisitClinicInvoiceEditData extends Record<string, unknown> {
  massage_price?: number;
  orthomorphia_price?: number;
  hot_treatment_price?: number;
  electric_treatment_price?: number;
  meisai_issue_fee?: number;
  treatment_report_price?: number;
  treatment_price?: number;
  madoguchi_price?: number;
  invoice?: number;
  default?: string;
}

/** GET /visit-clinic-invoices/edit/{visitId} */
export async function fetchR2VisitClinicInvoiceEdit(
  siteType: string,
  clinicNumber: string,
  visitId: number
): Promise<R2VisitClinicInvoiceEditData> {
  const res = await apiClient.get<{ success: boolean; data: R2VisitClinicInvoiceEditData }>(
    clinicApiPath(siteType, clinicNumber, `/visit-clinic-invoices/edit/${visitId}`)
  );
  return res.data.data;
}

/** POST /visit-clinic-invoices/save — SCR-183 §18 保存. */
export async function saveR2VisitClinicInvoice(
  siteType: string,
  clinicNumber: string,
  payload: Record<string, unknown>
): Promise<{ response: boolean; free_medical_treatment_fee?: number }> {
  const res = await apiClient.post<{
    success: boolean;
    data: { response: boolean; free_medical_treatment_fee?: number };
  }>(clinicApiPath(siteType, clinicNumber, '/visit-clinic-invoices/save'), payload);
  return res.data.data;
}

/**
 * SCR-182 一覧印刷 — POST /visit-clinic-invoices/r2-list-pdf.
 *
 * A2 posts to /patients/continuation/list/pdf, which reads `visits`; R2 has its own
 * endpoint. Same request shape, so the page only swaps the function.
 */
export async function printR2VisitClinicInvoiceListPdf(
  siteType: string,
  clinicNumber: string,
  payload: Record<string, unknown>
): Promise<Blob> {
  const res = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/visit-clinic-invoices/r2-list-pdf'),
    payload,
    { responseType: 'blob', headers: { Accept: 'application/pdf' } }
  );
  return res.data as Blob;
}
